/* THE D&A START CONVENTION -- one owner (2026-10-13).
 *
 * A per-COMPANY setting: when does an asset's depreciation / amortization schedule begin?
 *   'in_month'    -- the month the asset is placed in service (its purchase month). The default, and the behaviour of
 *                    every company before this setting existed.
 *   'next_month'  -- the month AFTER the asset is placed in service.
 * It moves ONLY where the schedule STARTS. The life is unchanged (5 years is still 60 monthly amounts), and the COST still
 * lands on the balance sheet in the purchase month under either value (engines/computeDA.js grossAdditions).
 *
 * WHO READS THIS FILE
 *   engines/computeDA.js       depreciationStartMonth -- the ONE place the offset is applied to a date
 *   ModelProvider.js           reads the stored value and hands it to runFullModel (and to rawData for Scenarios)
 *   pages/DAModulePage.js      the "Depreciation starts" control and the page's own schedule
 *   pages/AdminPage.js         the same control on the Step-8 asset screens
 *   server/diagnostics/verify-fullmodel.js   reads the stored value exactly as ModelProvider does
 *
 * WHERE IT IS STORED: onboarding_state.steps.da_start_convention -- a plain string under its OWN key of the per-company
 * `steps` object (an existing jsonb column; no schema change). The server's PUT /api/:companyId/onboarding-state MERGES
 * `steps` key by key, so sending this one key changes nothing else, and no wizard step writes this key (each step writes
 * its own slug), so a step save or a re-import cannot clear it. That route is super-admin-only on the server: the setting
 * is changed by the Finledom administrator and shown read-only to everyone else.
 *
 * ABSENT OR UNKNOWN = 'in_month'. Only the exact string 'next_month' moves a schedule; null, undefined, '' and any other
 * value behave exactly as before this file existed. Plain ASCII on purpose. */

export const DA_START_IN_MONTH = 'in_month';
export const DA_START_NEXT_MONTH = 'next_month';

export const DA_START_FIELD_LABEL = 'Depreciation starts';

export const DA_START_OPTIONS = [
  { id: DA_START_IN_MONTH, label: 'Month placed in service' },
  { id: DA_START_NEXT_MONTH, label: 'Month after placed in service' },
];

/* Any stored / passed value -> one of the two ids. */
export function normalizeDaStart(value) {
  return value === DA_START_NEXT_MONTH ? DA_START_NEXT_MONTH : DA_START_IN_MONTH;
}

/* How many months after the in-service month the schedule begins: 0 or 1. */
export function daStartOffsetMonths(value) {
  return normalizeDaStart(value) === DA_START_NEXT_MONTH ? 1 : 0;
}

export function daStartLabel(value) {
  const id = normalizeDaStart(value);
  return DA_START_OPTIONS.find(o => o.id === id).label;
}

/* ---- storage: the one reader and the one payload builder ---- */
export const DA_START_STEPS_KEY = 'da_start_convention';

/* The GET /api/:companyId/onboarding-state body (or the same row read from the database) -> the STORED value, untouched.
 * Never stored -> undefined (callers normalise; ModelProvider passes it through so a company that never chose carries no
 * new field). */
export function daStartFromOnboardingState(onboardingState) {
  const steps = onboardingState && onboardingState.steps;
  if (!steps || typeof steps !== 'object') return undefined;
  const v = steps[DA_START_STEPS_KEY];
  return v === null ? undefined : v;
}

/* The body for PUT /api/:companyId/onboarding-state that stores a choice. It names ONLY this key: the server merges
 * `steps`, and every other field of the row is kept when omitted. */
export function daStartSavePayload(value) {
  return { steps: { [DA_START_STEPS_KEY]: normalizeDaStart(value) } };
}
